// Fires a single tracking URL as a fire-and-forget 1×1 image request
function ping(url) {
  try {
    new Image().src = url;
  } catch (_) { /* ignore */ }
}

// Substitutes [MACRO] placeholders in a tracking URL
function substitute(url, macros) {
  let result = url;
  const urlEncoded = new Set(['PAGEURL', 'ASSETURI']);
  Object.entries(macros).forEach(([key, value]) => {
    const v = urlEncoded.has(key) ? encodeURIComponent(String(value)) : String(value);
    result = result.replace(new RegExp(`\\[${key}\\]`, 'g'), v);
  });
  return result;
}

export class VASTTracker {
  constructor(client, ad, creative) {
    this.client = client;
    this.ad = ad;
    this.creative = creative;

    this.skipDelay = creative?.skipDelay ?? -1;

    // Which one-time events have already been fired
    this._fired = new Set();

    // EventEmitter for internal hooks (firstQuartile, midpoint)
    this._listeners = {};
  }

  // Minimal EventEmitter support
  on(event, cb) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(cb);
  }

  _emit(event) {
    (this._listeners[event] || []).forEach((cb) => cb());
  }

  // Fire all URLs for a tracking event name, substituting macros
  _fire(eventName, macros = {}) {
    const urls = this.creative?.trackingEvents?.[eventName] ?? [];
    urls.forEach((url) => ping(substitute(url, macros)));
  }

  // Fire once — subsequent calls for the same event are no-ops
  _fireOnce(eventName, macros = {}) {
    if (this._fired.has(eventName)) return;
    this._fired.add(eventName);
    this._fire(eventName, macros);
    this._emit(eventName);
  }

  // ─── Public tracking API ───────────────────────────────────────────────────

  // Once per ad, not per tracker: an ad's linear, non-linear and companion creatives each get a
  // tracker of their own, and each non-linear or companion variation calls this again.
  trackImpression(macros = {}) {
    if (!this.ad || this.ad.impressionTracked) return;
    this.ad.impressionTracked = true;
    (this.ad.impressionUrls ?? []).forEach((url) => ping(substitute(url, macros)));
  }

  load(macros = {}) {
    this._fireOnce('loaded', macros);
  }

  overlayViewDuration(timecode, macros = {}) {
    this._fire('overlayViewDuration', { ...macros, ADPLAYHEAD: timecode });
  }

  // Called on every timeupdate — fires start / quartile events at the right thresholds
  setProgress(currentTime, macros = {}) {
    const { duration } = this.creative || {};
    if (!duration || duration <= 0) return;

    const pct = currentTime / duration;

    if (currentTime > 0) this._fireOnce('start', macros);
    if (pct >= 0.25) this._fireOnce('firstQuartile', macros);
    if (pct >= 0.5)  this._fireOnce('midpoint', macros);
    if (pct >= 0.75) this._fireOnce('thirdQuartile', macros);
  }

  setPaused(isPaused, macros = {}) {
    this._fire(isPaused ? 'pause' : 'resume', macros);
  }

  setMuted(isMuted, macros = {}) {
    this._fire(isMuted ? 'mute' : 'unmute', macros);
  }

  setFullscreen(isFullscreen) {
    this._fire(isFullscreen ? 'fullscreen' : 'exitFullscreen');
  }

  skip(macros = {}) {
    this._fireOnce('skip', macros);
  }

  complete(macros = {}) {
    this._fireOnce('complete', macros);
  }

  // url is unused (kept for API compatibility); fires click-tracking URLs
  click(_url, macros = {}) {
    (this.creative?.videoClickTrackingURLTemplates ?? []).forEach((url) =>
      ping(substitute(url, macros)),
    );
  }

  error(macros = {}) {
    (this.ad?.errorUrls ?? []).forEach((url) => ping(substitute(url, macros)));
  }

  close(macros = {}) {
    this._fire('close', macros);
    this._fire('closeLinear', macros);
  }

  verificationNotExecuted(_vendor, macros = {}) {
    this._fire('verificationNotExecuted', macros);
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  // Convert seconds to "HH:MM:SS.mmm"
  convertToTimecode(seconds = 0) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return [
      String(h).padStart(2, '0'),
      String(m).padStart(2, '0'),
      `${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`,
    ].join(':');
  }
}